import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
} from 'date-fns';

/**
 * Dates, in the reader's own language.
 *
 * Every method here used to call date-fns with no locale, so an Arabic dashboard printed
 * `Aug 29, 11:10 PM` under every single message — a Latin date, laid out left-to-right, inside a
 * right-aligned Arabic bubble. It is the most repeated string in the product and it was in the
 * wrong language and the wrong direction for the people who actually use it.
 *
 * `Intl.DateTimeFormat` rather than date-fns locales: it is built in, so it costs no bundle and
 * covers every locale the dashboard offers rather than the handful anyone would remember to
 * import. The five date-fns patterns these methods are actually called with are mapped to Intl
 * options below; anything else falls through to date-fns unchanged, so no call site can break
 * on a pattern this map has not seen.
 */
const INTL_BY_PATTERN = {
  'h:mm a': { hour: 'numeric', minute: '2-digit' },
  'MMM d, yyyy': { year: 'numeric', month: 'short', day: 'numeric' },
  'LLL d, h:mm a': {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
  'LLL d y, h:mm a': {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  },
  PPp: { dateStyle: 'long', timeStyle: 'short' },
};

// `pt_BR` is how the app names its locales; `Intl` wants `pt-BR`. Called with `this` undefined
// from the specs, which invoke these as plain functions — hence the optional chaining.
const resolveLocale = vm => (vm?.$i18n?.locale || 'en').replace('_', '-');

const localizedFormat = (vm, date, pattern) => {
  const options = INTL_BY_PATTERN[pattern];
  if (!options) return format(date, pattern);
  try {
    return new Intl.DateTimeFormat(resolveLocale(vm), options).format(date);
  } catch (error) {
    // An unknown locale tag throws rather than falling back. English is better than nothing.
    return new Intl.DateTimeFormat('en', options).format(date);
  }
};

export default {
  methods: {
    messageStamp(time, dateFormat = 'h:mm a') {
      return localizedFormat(this, fromUnixTime(time), dateFormat);
    },
    messageTimestamp(time, dateFormat = 'MMM d, yyyy') {
      const messageTime = fromUnixTime(time);
      if (!isSameYear(messageTime, new Date())) {
        return localizedFormat(this, messageTime, 'LLL d y, h:mm a');
      }
      return localizedFormat(this, messageTime, dateFormat);
    },
    dynamicTime(time) {
      // date-fns has no Intl equivalent for "about 2 years ago", so this one keeps date-fns and
      // its English. `dashboard/helper/relativeTime.js` is the localized replacement and is what
      // the conversation list already uses; the remaining callers here should move to it.
      return formatDistanceToNow(fromUnixTime(time), { addSuffix: true });
    },
    dateFormat(time, dateFormat = 'MMM d, yyyy') {
      return localizedFormat(this, fromUnixTime(time), dateFormat);
    },
  },
};
